import { defineMessages, IntlShape, MessageDescriptor } from 'react-intl'

import { AsyncThunk, ThunkDispatch } from '@reduxjs/toolkit'
import { addDays, format, max as maxDate, startOfDay } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { CountryCode as CountryIsoCode, parsePhoneNumber } from 'libphonenumber-js'
import { AnyAction } from 'redux'

import { NP_BOOKING_WINDOWS, NP_CLINICAL_INTAKE_BOOKING_WINDOW } from './constants'
import {
  NpBookingExitVariant,
  NpBookingStep,
  NpBookingStepMetadata,
  NpBookingSubtitleId,
  NpBookingTitleId,
  NpPatientBeingBookedFor,
} from './types'
import {
  AvailabilityDate,
  NPAppointment,
  NpAppointmentType,
  NpAvailableTimesRequest,
  NpAvailableTimesResponse,
  NpBookableSessionType,
  NpModalityType,
  NpScheduledSession,
  NpScheduleSessionsRequest,
  NpScheduleSessionsResponse,
  NpSessionsResponse,
} from '../../models/calendar/CalendarV2'
import { MEETING_FORMATS } from '../../models/session/Session'
import { PHONE_TYPES } from '../calendar/types'
import { assertNever } from '../common/utils/commonUtils'

export const NP_BOOKING_TITLE_MESSAGES: Record<NpBookingTitleId, MessageDescriptor> = defineMessages({
  [NpBookingTitleId.SELECT_PROVIDER]: {
    defaultMessage: 'Select a provider',
    description: 'NP booking header title shown when selecting an in-person provider',
  },
  [NpBookingTitleId.APPOINTMENT_PROGRESS]: {
    defaultMessage: 'Appointment {current} of {total}',
    description: 'NP booking header title showing which appointment number the user is currently scheduling',
  },
  [NpBookingTitleId.APPOINTMENTS_BOOKED]: {
    defaultMessage: 'Appointments booked',
    description: 'NP booking header title shown when all appointments have been booked',
  },
})

export const NP_BOOKING_SUBTITLE_MESSAGES: Record<NpBookingSubtitleId, MessageDescriptor> = defineMessages({
  [NpBookingSubtitleId.CLINICAL_INTAKE]: {
    defaultMessage: 'Clinical intake',
    description: 'NP booking header subtitle for the clinical intake appointment',
  },
  [NpBookingSubtitleId.TESTING_SESSION_1]: {
    defaultMessage: 'Testing session 1',
    description: 'NP booking header subtitle for the first testing session appointment',
  },
  [NpBookingSubtitleId.TESTING_SESSION_2]: {
    defaultMessage: 'Testing session 2',
    description: 'NP booking header subtitle for the second testing session appointment',
  },
})

export const NP_BOOKING_STEP_METADATA: Record<NpBookingStep, NpBookingStepMetadata> = {
  [NpBookingStep.SELECT_PROVIDER]: {
    titleId: NpBookingTitleId.SELECT_PROVIDER,
    showBackButton: true,
  },
  [NpBookingStep.CLINICAL_INTAKE_CC]: {
    appointmentNumber: 1,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.CLINICAL_INTAKE,
    showBackButton: true,
  },
  [NpBookingStep.CLINICAL_INTAKE_PAYMENT]: {
    appointmentNumber: 1,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.CLINICAL_INTAKE,
    showBackButton: true,
  },
  [NpBookingStep.CLINICAL_INTAKE_BOOKING]: {
    appointmentNumber: 1,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.CLINICAL_INTAKE,
    showBackButton: true,
  },
  [NpBookingStep.TESTING_SESSION_1_CC]: {
    appointmentNumber: 2,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.TESTING_SESSION_1,
    showBackButton: false,
    exitModalVariant: NpBookingExitVariant.TESTING_1,
  },
  [NpBookingStep.TESTING_SESSION_1_BOOKING]: {
    appointmentNumber: 2,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.TESTING_SESSION_1,
    showBackButton: true,
    exitModalVariant: NpBookingExitVariant.TESTING_1,
  },
  [NpBookingStep.TESTING_SESSION_2_CC]: {
    appointmentNumber: 3,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.TESTING_SESSION_2,
    showBackButton: false,
    exitModalVariant: NpBookingExitVariant.TESTING_2,
  },
  [NpBookingStep.TESTING_SESSION_2_BOOKING]: {
    appointmentNumber: 3,
    titleId: NpBookingTitleId.APPOINTMENT_PROGRESS,
    subtitleId: NpBookingSubtitleId.TESTING_SESSION_2,
    showBackButton: true,
    exitModalVariant: NpBookingExitVariant.TESTING_2,
  },
  [NpBookingStep.CONFIRMATION]: {
    titleId: NpBookingTitleId.APPOINTMENTS_BOOKED,
    showBackButton: false,
  },
}

export function getNpBookingProgress(stepPosition: number, totalSteps: number): number {
  return stepPosition / totalSteps
}

/**
 * Converts the NP available-times response into the `AvailabilityDate` shape used by the calendar booking UI.
 * Each slot arrives as a date (e.g. '2026-10-01') and a wall-clock start time (e.g. '15:00:00') expressed in the
 * response time zone, which is the user's time zone. This combines the two with that time zone to produce an ISO
 * datetime string (e.g. '2026-10-01T22:00:00.000Z') that the calendar formats back into the user's local time
 * (e.g. '3:00 PM') on each time slot button. Each slot keeps its `providerLyraId` so the booking call knows which
 * provider owns the selected time under pooled scheduling.
 */
export function mapNpAvailabilityToAvailabilityDates(
  response: NpAvailableTimesResponse | null | undefined,
): AvailabilityDate[] {
  if (!response?.availability?.length) {
    return []
  }
  return response.availability
    .filter(({ timeSlots }) => timeSlots?.length)
    .map(({ date, timeSlots }) => ({
      date,
      times: timeSlots.map(({ startTime, providerLyraId }) => ({
        datetime: getNpTimeSlotDatetime({ date, startTime, timeZone: response.timeZone }),
        providerLyraId,
      })),
    }))
}

/**
 * The UTC ISO start of an NP time slot, from the slot's date (e.g. '2026-10-01') and wall-clock start time (e.g.
 * '15:00:00') in the available-times response time zone. The provider results cards and the calendar both pick slots in
 * this form, so both store the selected appointment as the same UTC ISO string.
 */
export function getNpTimeSlotDatetime({
  date,
  startTime,
  timeZone,
}: {
  date: string
  startTime: string
  timeZone: string
}): string {
  return zonedTimeToUtc(`${date}T${startTime}`, timeZone).toISOString()
}

/**
 * The UTC ISO start of a booked NP session. The schedule-sessions response reports each session's start as a wall-clock
 * date and time in the member's time zone (the `timeZone` sent in the request), so this combines them the same way
 * `mapNpAvailabilityToAvailabilityDates` does for slots. Keeping every NP appointment start as a UTC ISO string means
 * the booked and still-selected appointments can be handled uniformly.
 */
export function getNpScheduledSessionDatetime({
  startDate,
  startTime,
  timeZone,
}: Pick<NpScheduledSession, 'startDate' | 'startTime'> & { timeZone: string }): string {
  return getNpTimeSlotDatetime({ date: startDate, startTime, timeZone })
}

/**
 * Builds the NP available-times request. `relativeTo` is the date of the previous appointment in the booking sequence,
 * which the booking window is calculated from. For example, when booking testing session 1, `relativeTo` is the date
 * of the clinical intake appointment. For the clinical intake appointment itself, `relativeTo` is always today.
 */
export function buildNpAvailableTimesRequest(
  request: Omit<NpAvailableTimesRequest, 'startDate' | 'endDate'>,
  relativeTo: Date,
): NpAvailableTimesRequest {
  // TODO: get the booking window for the request's session type from the booking rules endpoint
  const bookingWindow = NP_CLINICAL_INTAKE_BOOKING_WINDOW
  const relativeToDate = utcToZonedTime(relativeTo, request.timeZone)
  return {
    ...request,
    startDate: format(addDays(relativeToDate, bookingWindow.minDaysFromToday), 'yyyy-MM-dd'),
    endDate: format(addDays(relativeToDate, bookingWindow.maxDaysFromToday), 'yyyy-MM-dd'),
  }
}

/**
 * Effect body shared by the web and mobile calendar booking containers. Requests NP availability for the current
 * user and returns a cleanup that aborts the request, so a response that lands after leaving can't write stale
 * availability into the store, and clears the stored availability so a return visit doesn't briefly show stale data.
 * Each app passes its own dispatch, thunk, and clear action since those are defined per store. See
 * `buildNpAvailableTimesRequest` for how `relativeTo` is used.
 */
export function runNpAvailableTimesEffect({
  dispatch,
  getNpAvailableTimes,
  clearNpAvailableTimes,
  userLyraId,
  providerLyraId,
  sessionType,
  modalityType,
  userTimeZone,
  relativeTo,
}: {
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>
  getNpAvailableTimes: AsyncThunk<NpAvailableTimesResponse, NpAvailableTimesRequest, {}>
  clearNpAvailableTimes: () => AnyAction
  userLyraId: string | undefined
  providerLyraId?: string
  sessionType: NpAppointmentType
  modalityType: NpModalityType
  userTimeZone: string
  relativeTo: Date
}): (() => void) | undefined {
  if (!userLyraId) {
    return
  }
  const request = dispatch(
    getNpAvailableTimes(
      buildNpAvailableTimesRequest(
        { userLyraId, providerLyraId, sessionType, modalityType, timeZone: userTimeZone },
        relativeTo,
      ),
    ),
  )
  return () => {
    request.abort()
    dispatch(clearNpAvailableTimes())
  }
}

const DATE_ONLY_FORMAT = 'yyyy-MM-dd'

export type NpAvailabilityWindow = {
  startDate: string // yyyy-MM-dd
  endDate: string // yyyy-MM-dd
}

/**
 * The date range to request availability for, based on the appointment the member is booking (see NP_BOOKING_WINDOWS):
 * clinical intake is anchored on today, testing session 1 on the clinical intake date, and testing session 2 on the
 * testing session 1 date. Returns null when the anchor appointment is missing, since there is no valid window to request.
 * The start is never before today in case the anchor is in the past (e.g. the member returns to book testing later).
 */
export function getNpAvailabilityWindow({
  sessionType,
  timeZone,
  today = new Date(),
  clinicalIntakeDatetime,
  testingSession1Datetime,
}: {
  sessionType: NpBookableSessionType
  timeZone: string
  today?: Date
  clinicalIntakeDatetime?: string // UTC ISO
  testingSession1Datetime?: string // UTC ISO
}): NpAvailabilityWindow | null {
  // Calendar-day arithmetic is done on "zoned" Dates whose local fields reflect the member's time zone
  const toDayInZone = (datetime: string | Date) => startOfDay(utcToZonedTime(datetime, timeZone))
  const todayInZone = toDayInZone(today)
  let anchor: Date | null
  switch (sessionType) {
    case NpAppointmentType.NP_CLINICAL_INTAKE:
      anchor = todayInZone
      break
    case NpAppointmentType.NP_TESTING_SESSION_1:
      anchor = clinicalIntakeDatetime ? toDayInZone(clinicalIntakeDatetime) : null
      break
    case NpAppointmentType.NP_TESTING_SESSION_2:
      anchor = testingSession1Datetime ? toDayInZone(testingSession1Datetime) : null
      break
    default:
      return assertNever(sessionType)
  }
  if (!anchor) {
    return null
  }
  // TODO: use the scheduling rules from the /rules endpoint instead of NP_BOOKING_WINDOWS once it is available
  const { minDays, maxDays } = NP_BOOKING_WINDOWS[sessionType]
  return {
    startDate: format(maxDate([addDays(anchor, minDays), todayInZone]), DATE_ONLY_FORMAT),
    endDate: format(addDays(anchor, maxDays), DATE_ONLY_FORMAT),
  }
}

/**
 * The appointment the member books after `sessionType`, or null after the last one. The member books the clinical
 * intake, then testing session 1, then testing session 2.
 */
export function getNpNextSessionType(sessionType: NpBookableSessionType): NpBookableSessionType | null {
  switch (sessionType) {
    case NpAppointmentType.NP_CLINICAL_INTAKE:
      return NpAppointmentType.NP_TESTING_SESSION_1
    case NpAppointmentType.NP_TESTING_SESSION_1:
      return NpAppointmentType.NP_TESTING_SESSION_2
    case NpAppointmentType.NP_TESTING_SESSION_2:
      return null
    default:
      return assertNever(sessionType)
  }
}

/**
 * Builds the `NPAppointment` the NP calendar and booking pages take, with the prior appointment dates rendered for
 * display (e.g. 'Feb 7') in the member's time zone. Missing anchors render as an empty string so the page still renders.
 */
export function getNpAppointmentData({
  sessionType,
  clinicalIntakeDatetime,
  testingSession1Datetime,
  timeZone,
  formatDate,
}: {
  sessionType: NpBookableSessionType
  clinicalIntakeDatetime?: string // UTC ISO
  testingSession1Datetime?: string // UTC ISO
  timeZone: string
  formatDate: IntlShape['formatDate']
}): NPAppointment {
  const displayOptions = { month: 'short', day: 'numeric', timeZone } as const
  const toDisplayDate = (datetime?: string) => (datetime ? formatDate(datetime, displayOptions) : '')
  switch (sessionType) {
    case NpAppointmentType.NP_CLINICAL_INTAKE:
      return { sessionType }
    case NpAppointmentType.NP_TESTING_SESSION_1:
      return { sessionType, clinicalIntakeDate: toDisplayDate(clinicalIntakeDatetime) }
    case NpAppointmentType.NP_TESTING_SESSION_2:
      return {
        sessionType,
        clinicalIntakeDate: toDisplayDate(clinicalIntakeDatetime),
        testingSession1Date: toDisplayDate(testingSession1Datetime),
      }
    default:
      return assertNever(sessionType)
  }
}

/**
 * Narrows an NP session type to one the member books themselves, or null for the ones the provider schedules (testing
 * session 3 and the feedback session). Used to turn the episode's `nextSessionType` into the appointment to book.
 */
export function getNpBookableSessionType(sessionType: NpAppointmentType | null): NpBookableSessionType | null {
  switch (sessionType) {
    case NpAppointmentType.NP_CLINICAL_INTAKE:
    case NpAppointmentType.NP_TESTING_SESSION_1:
    case NpAppointmentType.NP_TESTING_SESSION_2:
      return sessionType
    default:
      return null
  }
}

/**
 * Reduces the member's NP episode sessions to the booked ones, in the shape the schedule-sessions response uses, so a
 * member returning to the booking flow (e.g. after cancelling or leaving part way) picks up with the same state a
 * booking in the same session would have left behind. Null when the member has no active episode, which is the same
 * as nothing having been booked yet.
 */
export function mapNpSessionsToScheduledSessions({
  episodeId,
  sessions,
}: NpSessionsResponse): NpScheduleSessionsResponse | null {
  if (episodeId === null) {
    return null
  }
  return {
    episodeId,
    scheduledSessions: sessions.flatMap(({ appointment }) => (appointment ? [appointment] : [])),
  }
}

export type NpSessionToSchedule = {
  sessionType: NpAppointmentType
  providerLyraId: string
  appointmentStart: Date
  modality: NpModalityType.VIRTUAL | NpModalityType.IN_PERSON
}

/**
 * Builds the body for POST /v2/calendar/{userLyraId}/schedule-sessions. Session start dates and times are sent as
 * wall-clock values in `timeZone` (the member's time zone), matching how available-times reports them. Phone fields
 * are handled the same way as the S4C booking payload (see useBookingPayload).
 */
export function buildNpScheduleSessionsRequest({
  timeZone,
  episodeId,
  phone,
  countryIsoCode,
  phoneType,
  smsConsent,
  sessions,
}: {
  timeZone: string
  episodeId?: string
  phone?: string | null
  countryIsoCode: CountryIsoCode
  phoneType: PHONE_TYPES
  smsConsent: boolean
  sessions: NpSessionToSchedule[] // In chronological order
}): NpScheduleSessionsRequest {
  const phoneNumberInfo = phone ? parsePhoneNumber(phone, countryIsoCode) : null
  return {
    timeZone,
    episodeId,
    phone: phoneNumberInfo?.number,
    phoneCountryCode: countryIsoCode,
    phoneType,
    smsConsent,
    sessions: sessions.map(({ sessionType, providerLyraId, appointmentStart, modality }) => ({
      sessionType,
      lyraProviderId: providerLyraId,
      startDate: formatInTimeZone(appointmentStart, timeZone, DATE_ONLY_FORMAT),
      startTime: formatInTimeZone(appointmentStart, timeZone, 'HH:mm:ss'),
      meetingFormat: modality === NpModalityType.IN_PERSON ? MEETING_FORMATS.IN_PERSON : MEETING_FORMATS.VIDEO,
    })),
  }
}

/**
 * Builds the dependent props the NP booking pages take. A page shows "{name}'s" copy when booking for a
 * dependent and "your" copy otherwise.
 */
export function getNpDependentBookingProps(
  patient: NpPatientBeingBookedFor | null,
): { isBookingForDependent: true; dependentName: string } | { isBookingForDependent: false } {
  return patient?.isDependent
    ? { isBookingForDependent: true, dependentName: patient.name }
    : { isBookingForDependent: false }
}

/**
 * Whether the member adds a payment method before booking the session being scheduled. As in S4C, only members whose
 * sessions bill through their health plan see the payment page, and in the NP flow only before the clinical intake:
 * the testing sessions are booked right after it with the same card on file.
 */
export function getShouldShowNpPaymentPage({
  sessionType,
  isHPIRequired,
}: {
  sessionType: NpBookableSessionType | null
  isHPIRequired: boolean
}): boolean {
  return isHPIRequired && sessionType === NpAppointmentType.NP_CLINICAL_INTAKE
}
